import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { Dropdown, Form, Badge, Spinner } from 'react-bootstrap';

import { useLanguage } from '../../context/LanguageProvider/LanguageProvider';
import { useAuth } from '../../context/AuthProvider/AuthProvider';
import { useSiteSettings } from '../../context/SiteSettingsProvider/SiteSettingsProvider';
import NioIcon from '../../components/NioIcon/NioIcon';

const NOTIFICATION_SEED = () => [
  {
    id: 'n1',
    read: false,
    to: '/admin/users',
    titleKey: 'admin.notifications.n1Title',
    bodyKey: 'admin.notifications.n1Body',
    timeKey: 'admin.notifications.timeRecent',
  },
  {
    id: 'n2',
    read: false,
    to: '/admin/projects',
    titleKey: 'admin.notifications.n2Title',
    bodyKey: 'admin.notifications.n2Body',
    timeKey: 'admin.notifications.timeRecent',
  },
  {
    id: 'n3',
    read: false,
    to: '/admin/projects',
    titleKey: 'admin.notifications.n3Title',
    bodyKey: 'admin.notifications.n3Body',
    timeKey: 'admin.notifications.timeEarlier',
  },
];

const NotifToggle = forwardRef(({ className, onClick, badgeCount, ariaLabel }, ref) => (
  <button
    ref={ref}
    type="button"
    className={classNames('btn btn-light border jassp-admin-icon-btn position-relative', className)}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <NioIcon name="bell" className="fs-5" variant="dark" />
    {badgeCount > 0 ? (
      <Badge bg="danger" pill className="position-absolute top-0 start-100 translate-middle p-1 border border-white">
        {badgeCount > 9 ? '9+' : badgeCount}
      </Badge>
    ) : null}
  </button>
));
NotifToggle.displayName = 'NotifToggle';

const LangToggle = forwardRef(({ className, onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className={classNames('btn btn-light border jassp-admin-icon-btn', className)}
    onClick={onClick}
    aria-label="Language"
  >
    <NioIcon name="globe" className="fs-5" variant="dark" />
  </button>
));
LangToggle.displayName = 'LangToggle';

const NAV = [
  { to: '/admin', end: true, key: 'admin.nav.dashboard' },
  { to: '/admin/projects', key: 'admin.nav.projects' },
  { to: '/admin/domains', key: 'admin.nav.domains' },
  { to: '/admin/users', key: 'admin.nav.users' },
  { to: '/admin/settings', key: 'admin.nav.settings' },
];

export default function AdminLayout() {
  const { t, locale, setLocale } = useLanguage();
  const { logout, isAuthenticated, isAdmin, session, ready } = useAuth();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(() => NOTIFICATION_SEED());

  useEffect(() => {
    if (!ready) return;
    if (!isAuthenticated) {
      navigate('/auth/login', { replace: true, state: { redirectTo: '/admin' } });
      return;
    }
    if (!isAdmin) {
      navigate('/', { replace: true });
    }
  }, [ready, isAuthenticated, isAdmin, navigate]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const markNotificationRead = useCallback((id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const markAllNotificationsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  }, []);

  const handleNotificationClick = useCallback(
    (notif) => {
      markNotificationRead(notif.id);
      if (notif.to) {
        navigate(notif.to);
      }
    },
    [markNotificationRead, navigate],
  );

  useEffect(() => {
    document.title = `${t('pages.admin.docTitle')} — ${t('layout.docSuffix')}`;
  }, [t]);

  const navItems = useMemo(
    () =>
      NAV.map((item) => ({
        ...item,
        label: t(item.key),
      })),
    [t],
  );

  if (!ready) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  const profileInitial = (session?.name || session?.email || 'A').trim().charAt(0).toUpperCase() || 'A';

  return (
    <div className="jassp-admin-root d-flex min-vh-100">
      <aside className="jassp-admin-sidebar d-flex flex-column flex-shrink-0">
        <div className="jassp-admin-sidebar-brand px-3 py-4">
          <Link to="/admin" className="text-white text-decoration-none fw-bold fs-5 d-flex align-items-center gap-2">
            <img src={settings.siteLogoUrl || '/images/logo-jassp.png'} alt="" className="jassp-admin-sidebar-logo" />
            <span className="lh-sm">{t('admin.layout.brand')}</span>
          </Link>
        </div>
        <nav className="jassp-admin-nav flex-grow-1 px-2 pb-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                classNames('jassp-admin-nav-link d-block rounded-2 px-3 py-2 mb-1 text-decoration-none', {
                  active: isActive,
                })
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="jassp-admin-sidebar-foot px-3 py-3 border-top border-white border-opacity-10">
          <Link
            to="/"
            className="jassp-admin-nav-link d-block rounded-2 px-3 py-2 mb-2 text-decoration-none small"
          >
            <NioIcon name="arrow-left" className="me-2" />
            {t('admin.nav.publicSite')}
          </Link>
          <button
            type="button"
            className="jassp-admin-nav-link jassp-admin-logout w-100 text-start border-0 rounded-2 px-3 py-2 bg-transparent"
            onClick={() => {
              logout();
              navigate('/auth/login', { replace: true });
            }}
          >
            <NioIcon name="signout" className="me-2" />
            {t('admin.nav.logout')}
          </button>
        </div>
      </aside>

      <div className="jassp-admin-main flex-grow-1 d-flex flex-column min-w-0">
        <header className="jassp-admin-topbar border-bottom bg-white px-3 px-lg-4 py-3 d-flex flex-wrap align-items-center gap-3">
          <Form className="jassp-admin-search flex-grow-1" onSubmit={(e) => e.preventDefault()}>
            <div className="position-relative">
              <NioIcon name="search" className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" />
              <Form.Control
                type="search"
                placeholder={t('admin.layout.searchPlaceholder')}
                className="ps-5 rounded-pill border-gray-200"
              />
            </div>
          </Form>
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Dropdown align="end">
              <Dropdown.Toggle as={LangToggle} id="admin-lang" />
              <Dropdown.Menu>
                <Dropdown.Header className="small text-muted">{t('nav.langMenuTitle')}</Dropdown.Header>
                <Dropdown.Item active={locale === 'fr'} onClick={() => setLocale('fr')}>
                  {t('nav.langFr')}
                </Dropdown.Item>
                <Dropdown.Item active={locale === 'en'} onClick={() => setLocale('en')}>
                  {t('nav.langEn')}
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Dropdown align="end">
              <Dropdown.Toggle
                as={NotifToggle}
                id="admin-notifications"
                badgeCount={unreadCount}
                ariaLabel={t('admin.top.notifications')}
              />
              <Dropdown.Menu className="jassp-admin-notif-menu shadow border-0 py-0">
                <div className="px-3 py-2 border-bottom bg-light rounded-top">
                  <span className="fw-semibold small">{t('admin.top.notifications')}</span>
                  {unreadCount > 0 ? (
                    <Badge bg="primary" pill className="ms-2 align-middle">
                      {unreadCount}
                    </Badge>
                  ) : null}
                </div>
                <div className="jassp-admin-notif-list">
                  {notifications.length === 0 ? (
                    <div className="px-3 py-4 text-muted small text-center">{t('admin.notifications.empty')}</div>
                  ) : (
                    notifications.map((n) => (
                      <Dropdown.Item
                        key={n.id}
                        as="button"
                        type="button"
                        className={classNames('jassp-admin-notif-item text-wrap py-3 border-bottom border-light w-100 text-start', {
                          'bg-primary bg-opacity-10': !n.read,
                        })}
                        onClick={() => handleNotificationClick(n)}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                          <span className="fw-semibold small text-dark">{t(n.titleKey)}</span>
                          {!n.read ? (
                            <span className="flex-shrink-0 rounded-circle bg-primary" style={{ width: 8, height: 8 }} title="" />
                          ) : null}
                        </div>
                        <p className="small text-muted mb-1 lh-sm">{t(n.bodyKey)}</p>
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {t(n.timeKey)}
                        </span>
                      </Dropdown.Item>
                    ))
                  )}
                </div>
                {notifications.some((n) => !n.read) ? (
                  <>
                    <Dropdown.Divider className="my-0" />
                    <Dropdown.Item
                      className="text-center small py-2 text-primary fw-semibold"
                      onClick={markAllNotificationsRead}
                    >
                      {t('admin.notifications.markAllRead')}
                    </Dropdown.Item>
                  </>
                ) : null}
              </Dropdown.Menu>
            </Dropdown>

            <div className="d-flex align-items-center gap-2 border rounded-pill ps-1 pe-3 py-1 bg-light">
              <span
                className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center fw-semibold jassp-admin-avatar"
                aria-hidden
              >
                {profileInitial}
              </span>
              <div className="small lh-sm d-none d-sm-block">
                <div className="fw-semibold text-dark">{session?.name || t('admin.top.profile')}</div>
                <div className="text-muted text-capitalize">{session?.role || 'admin'}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="jassp-admin-content flex-grow-1 p-3 p-lg-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
